import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BellRing, Download, RotateCcw } from 'lucide-react';
import {
  Carte,
  Chargement,
  Compteur,
  EnteteEcran,
  MessageErreur,
  Pagination,
  Pastille,
  PastilleSite,
} from '../components/ui/Primitives';
import { ChampFiltre, ChampRecherche } from '../components/ui/Onglets';
import { BarreSites } from '../components/ui/BarreSites';
import { FicheFacture } from '../components/factures/FicheFacture';
import { ModaleExportFactures } from '../components/factures/ModaleExportFactures';
import { usePerimetre } from '../site/usePerimetre';
import { TOUS_LES_SITES } from '../site/context';
import { useTaillePage } from '../lib/useTaillePage';
import { ErreurApi } from '../lib/api';
import { formaterCentimes, useFinancements } from '../lib/useFinancements';
import {
  useListeFactures,
  type EtatEnvoi,
  type EtatFacture,
  type LigneSuiviFacture,
  type TypeClient,
} from '../lib/useFactures';
import type { TypeFacturation } from '../lib/useFinancements';

/** « Non réglées » réunit ce qui doit encore : émise, partiellement réglée, en retard. */
type FiltreEtat = EtatFacture | 'NON_REGLEE' | '';

interface Filtres {
  recherche: string;
  typeClient: TypeClient | '';
  /** Apprentissage, contrat de professionnalisation, France Travail (TF-465). */
  typeFacturation: TypeFacturation | '';
  client: string;
  etat: FiltreEtat;
  envoi: EtatEnvoi | '';
  /** Bornes de la date d'émission, `AAAA-MM-JJ`, incluses. */
  du: string;
  au: string;
}

const SANS_FILTRE: Filtres = {
  recherche: '',
  typeClient: '',
  typeFacturation: '',
  client: '',
  etat: '',
  envoi: '',
  du: '',
  au: '',
};

const ETATS_NON_REGLES: ReadonlySet<EtatFacture> = new Set(['EMISE', 'PARTIELLEMENT_REGLEE', 'EN_RETARD']);

function jourFrancais(iso: string): string {
  const [annee, mois, jour] = iso.slice(0, 10).split('-');
  return `${jour}/${mois}/${annee}`;
}

/** Comparaison sans casse ni accent, pour la recherche. */
function cle(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

function retenue(ligne: LigneSuiviFacture, filtres: Filtres): boolean {
  if (filtres.typeClient && ligne.client.type !== filtres.typeClient) return false;
  if (filtres.typeFacturation && ligne.typeFacturation?.valeur !== filtres.typeFacturation) return false;
  if (filtres.client && ligne.client.cle !== filtres.client) return false;
  if (filtres.etat === 'NON_REGLEE') {
    if (ligne.nature !== 'FACTURE' || !ETATS_NON_REGLES.has(ligne.situation.etat)) return false;
  } else if (filtres.etat && ligne.situation.etat !== filtres.etat) {
    return false;
  }
  if (filtres.envoi && ligne.envoi?.etat !== filtres.envoi) return false;
  // Une période porte sur le jour d'émission : un brouillon n'en a pas encore.
  if (filtres.du && (!ligne.emiseLe || ligne.emiseLe < filtres.du)) return false;
  if (filtres.au && (!ligne.emiseLe || ligne.emiseLe > filtres.au)) return false;

  const cherche = cle(filtres.recherche.trim());
  if (!cherche) return true;
  return [
    `${ligne.apprenant.prenom} ${ligne.apprenant.nom}`,
    `${ligne.apprenant.nom} ${ligne.apprenant.prenom}`,
    ligne.numero ?? '',
    ligne.client.nom ?? '',
    ligne.reference ?? '',
    ligne.numeroProForma ?? '',
    ligne.formation ?? '',
  ].some((valeur) => cle(valeur).includes(cherche));
}

/**
 * Écran « Factures » (phase 4, TF-422 ; pas de Tiime) : toutes les factures et
 * tous les avoirs des antennes du lecteur, brouillons compris — le bloc
 * Financement du dossier les tient contrat par contrat, ici le pôle facturation
 * les suit ensemble.
 *
 * Le ticket demandait un statut « remonté depuis Tiime ». Sans Tiime, le pôle
 * le tient lui-même : il **émet** depuis le dossier, **note l'envoi** de la
 * pièce à son client (portail de l'OPCO, Chorus Pro, mail, courrier) et
 * **saisit les règlements** reçus ; l'état — émise, partiellement réglée,
 * réglée, en retard, annulée — s'en déduit. La fiche d'une pièce
 * (`?facture=<id>`, le lien de l'alerte « Facture en retard ») porte ces
 * gestes, le **rejet** d'une pièce par son client (« à renvoyer »), les
 * **notes de suivi** et le fil de ses gestes (TF-403 rouverte).
 *
 * État, reste dû, envoi et compteurs viennent de l'API
 * (`domain/suivi-factures.ts`) ; l'écran filtre et pagine ce qu'elle rend. Les
 * contenus de la maquette « Facturation électronique » (Factur-X, circuit
 * PDP/PPF, échéancier 40/30/30) ne sont pas repris : aucun n'existe.
 *
 * « Exporter » (TF-423, le « fichier pour import dans Tiime » relu sans Tiime)
 * sort la liste filtrée, toutes pages comprises, dans un fichier : pièces
 * émises ou règlements, classeur ou CSV, colonnes au choix.
 */
export function FacturesPage(): JSX.Element {
  const { site } = usePerimetre();
  const { data, isLoading, error } = useListeFactures(site);
  const [parametres, setParametres] = useSearchParams();
  const ouverte = parametres.get('facture');
  const [filtres, setFiltres] = useState<Filtres>(SANS_FILTRE);
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();
  const [exportOuvert, setExportOuvert] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [site, filtres, taillePage]);

  const ouvrir = useCallback(
    (id: string | null) => {
      setParametres(
        (avant) => {
          const suivants = new URLSearchParams(avant);
          if (id) suivants.set('facture', id);
          else suivants.delete('facture');
          return suivants;
        },
        { replace: true },
      );
    },
    [setParametres],
  );

  const changer = <K extends keyof Filtres>(champ: K, valeur: Filtres[K]): void =>
    setFiltres((avant) => ({
      ...avant,
      [champ]: valeur,
      // Un client d'un autre type que celui choisi ne resterait jamais sélectionnable.
      ...(champ === 'typeClient' ? { client: '' } : {}),
    }));

  const lignes = useMemo(
    () => (data?.lignes ?? []).filter((ligne) => retenue(ligne, filtres)),
    [data?.lignes, filtres],
  );
  const total = lignes.length;
  const pages = Math.max(1, Math.ceil(total / taillePage));
  const affichees = useMemo(() => {
    const debut = (page - 1) * taillePage;
    return lignes.slice(debut, debut + taillePage);
  }, [lignes, page, taillePage]);

  const clients = (data?.clients ?? []).filter((un) => !filtres.typeClient || un.type === filtres.typeClient);
  const filtre = Object.entries(filtres).some(([, valeur]) => valeur !== '');
  const compteurs = data?.compteurs;

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Factures"
        sousTitre="Pôle facturation · factures et avoirs émis par la plateforme, envoi aux financeurs et règlements reçus"
      />

      <BarreSites />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="À émettre"
          valeur={compteurs?.aEmettre ?? '—'}
          detail={[
            site === TOUS_LES_SITES ? 'toutes antennes' : `antenne ${site}`,
            'brouillons à émettre depuis le dossier',
          ].join(' · ')}
          ton="warm"
        />
        <Compteur
          libelle="À envoyer"
          valeur={compteurs?.aEnvoyer ?? '—'}
          detail={
            compteurs && compteurs.aRenvoyer > 0
              ? `dont ${compteurs.aRenvoyer} rejetée${compteurs.aRenvoyer > 1 ? 's' : ''} à renvoyer`
              : 'émises, envoi au client non noté'
          }
          ton="blue"
        />
        <Compteur
          libelle="Reste dû"
          valeur={compteurs?.nonRegleesTexte ?? '—'}
          detail={
            compteurs
              ? `${compteurs.nonReglees.nombre} facture${compteurs.nonReglees.nombre > 1 ? 's' : ''} non réglée${
                  compteurs.nonReglees.nombre > 1 ? 's' : ''
                }`
              : undefined
          }
          ton="navy"
        />
        <Compteur
          libelle="En retard"
          valeur={compteurs?.enRetard.nombre ?? '—'}
          detail={compteurs ? `${compteurs.enRetardTexte} échus` : undefined}
          ton="red"
        />
      </div>

      <BandeauAPreparer site={site} />

      <Carte
        titre="Factures et avoirs"
        actions={
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <ChampRecherche
              valeur={filtres.recherche}
              onChange={(valeur) => changer('recherche', valeur)}
              etiquette="Rechercher une facture"
              placeholder="Apprenant, n°, client, réf. DK"
            />
            <button
              type="button"
              onClick={() => setExportOuvert(true)}
              disabled={!data || total === 0}
              className="inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-codialis-navy transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              <Download className="h-4 w-4" aria-hidden />
              Exporter
            </button>
          </div>
        }
        sansBordure
      >
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-4 py-3 sm:px-6">
          <ChampFiltre
            valeur={filtres.typeFacturation}
            onChange={(valeur) => changer('typeFacturation', valeur as TypeFacturation | '')}
            etiquette="Filtrer par type de facturation"
          >
            <option value="">Toutes les facturations</option>
            {data?.filtres.typesFacturation.map((option) => (
              <option key={option.valeur} value={option.valeur}>
                {option.libelle}
              </option>
            ))}
          </ChampFiltre>
          <ChampFiltre
            valeur={filtres.typeClient}
            onChange={(valeur) => changer('typeClient', valeur as TypeClient | '')}
            etiquette="Filtrer par type de client"
          >
            <option value="">Tous les clients</option>
            {data?.filtres.typesClient.map((option) => (
              <option key={option.valeur} value={option.valeur}>
                {option.libelle}
              </option>
            ))}
          </ChampFiltre>
          <ChampFiltre
            valeur={filtres.client}
            onChange={(valeur) => changer('client', valeur)}
            etiquette="Filtrer par client"
          >
            <option value="">{filtres.typeClient ? 'Tous ces clients' : 'Chaque client'}</option>
            {clients.map((option) => (
              <option key={option.cle} value={option.cle}>
                {option.nom}
              </option>
            ))}
          </ChampFiltre>
          <ChampFiltre
            valeur={filtres.etat}
            onChange={(valeur) => changer('etat', valeur as FiltreEtat)}
            etiquette="Filtrer par état"
          >
            <option value="">Tous les états</option>
            <option value="NON_REGLEE">Non réglées (reste dû)</option>
            {data?.filtres.etats.map((option) => (
              <option key={option.valeur} value={option.valeur}>
                {option.libelle}
              </option>
            ))}
          </ChampFiltre>
          <ChampFiltre
            valeur={filtres.envoi}
            onChange={(valeur) => changer('envoi', valeur as EtatEnvoi | '')}
            etiquette="Filtrer par envoi"
          >
            <option value="">Tous les envois</option>
            {data?.filtres.envois.map((option) => (
              <option key={option.valeur} value={option.valeur}>
                {option.libelle}
              </option>
            ))}
          </ChampFiltre>
          {/* Les deux bornes de la période vont à la ligne ensemble. */}
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <ChampDate
              libelle="Émises du"
              valeur={filtres.du}
              max={filtres.au || undefined}
              onChange={(valeur) => changer('du', valeur)}
            />
            <ChampDate
              libelle="au"
              valeur={filtres.au}
              min={filtres.du || undefined}
              onChange={(valeur) => changer('au', valeur)}
            />
          </div>
        </div>

        {error && (
          <div className="p-4 sm:p-6">
            <MessageErreur
              message={error instanceof ErreurApi ? error.message : 'Les factures n’ont pas pu être chargées.'}
            />
          </div>
        )}
        {isLoading && <Chargement libelle="Chargement des factures…" />}

        {!isLoading && !error && (
          <Synthese lignes={lignes} filtre={filtre} onReinitialiser={() => setFiltres(SANS_FILTRE)} />
        )}

        {!isLoading && !error && total === 0 && (
          <p className="px-4 py-12 text-center text-sm text-codialis-gray sm:px-6">
            {data && data.lignes.length === 0
              ? 'Aucune facture ni aucun avoir dans ce périmètre : les factures se préparent depuis le bloc Financement du dossier.'
              : 'Aucune pièce ne correspond aux filtres actifs.'}
          </p>
        )}

        {/* Sous l'ordinateur, une carte par pièce : le tableau y défilerait de côté. */}
        {!isLoading && !error && total > 0 && (
          <ul className="divide-y divide-gray-100 lg:hidden">
            {affichees.map((ligne) => (
              <li key={ligne.id} className="space-y-2 px-4 py-4 sm:px-6">
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <Piece ligne={ligne} onOuvrir={() => ouvrir(ligne.id)} />
                  </div>
                  <Montant ligne={ligne} />
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <Apprenant ligne={ligne} />
                  <Client ligne={ligne} />
                </div>
                <Etats ligne={ligne} />
                <button
                  type="button"
                  onClick={() => ouvrir(ligne.id)}
                  className="inline-flex min-h-[44px] items-center text-xs font-semibold text-codialis-sky hover:underline"
                >
                  Ouvrir la fiche
                </button>
              </li>
            ))}
          </ul>
        )}

        {!isLoading && !error && total > 0 && (
          <div className="relative hidden overflow-x-auto lg:block">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="border-b border-gray-100 bg-gray-50">
                <tr>
                  {['Pièce', 'Apprenant', 'Client', 'Montant TTC', 'État · envoi', ''].map((colonne, index) => (
                    <th
                      key={colonne || index}
                      className={`px-3 py-3 text-[11px] font-bold uppercase tracking-wider text-codialis-gray xl:px-4 ${
                        colonne === 'Montant TTC' ? 'text-right' : 'text-left'
                      }`}
                    >
                      {colonne || <span className="sr-only">Actions</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {affichees.map((ligne) => (
                  <tr key={ligne.id} className="align-top transition hover:bg-gray-50">
                    <td className="px-3 py-3 xl:px-4">
                      <Piece ligne={ligne} onOuvrir={() => ouvrir(ligne.id)} />
                    </td>
                    <td className="px-3 py-3 xl:px-4">
                      <Apprenant ligne={ligne} />
                    </td>
                    <td className="px-3 py-3 xl:px-4">
                      <Client ligne={ligne} />
                    </td>
                    <td className="px-3 py-3 text-right xl:px-4">
                      <Montant ligne={ligne} />
                    </td>
                    <td className="max-w-[260px] px-3 py-3 xl:px-4">
                      <Etats ligne={ligne} />
                    </td>
                    <td className="px-3 py-3 text-right xl:px-4">
                      <button
                        type="button"
                        onClick={() => ouvrir(ligne.id)}
                        className="whitespace-nowrap text-xs font-semibold text-codialis-sky hover:underline"
                      >
                        Ouvrir la fiche
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!isLoading && !error && total > 0 && (
          <Pagination
            page={page}
            pages={pages}
            total={total}
            affiches={affichees.length}
            taillePage={taillePage}
            onPage={setPage}
            onChangerTaille={setTaillePage}
            uniteLabel="pièce"
          />
        )}
      </Carte>

      {ouverte && <FicheFacture id={ouverte} onFermer={() => ouvrir(null)} />}
      {exportOuvert && data && (
        <ModaleExportFactures
          referentiel={data.export}
          lignes={lignes}
          site={site}
          onFermer={() => setExportOuvert(false)}
        />
      )}
    </div>
  );
}

function ChampDate({
  libelle,
  valeur,
  min,
  max,
  onChange,
}: {
  libelle: string;
  valeur: string;
  min?: string | undefined;
  max?: string | undefined;
  onChange: (valeur: string) => void;
}): JSX.Element {
  return (
    <label className="flex w-full min-w-0 items-center gap-2 sm:w-auto">
      <span className="w-16 shrink-0 text-xs text-codialis-gray sm:w-auto">{libelle}</span>
      <input
        type="date"
        value={valeur}
        min={min}
        max={max}
        onChange={(evenement) => onChange(evenement.target.value)}
        className="min-h-[44px] w-full min-w-0 flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs sm:w-40 sm:flex-none"
      />
    </label>
  );
}

/**
 * Ce que montrent les filtres : nombre de pièces, factures, avoirs et ce qui
 * reste dû — l'addition des montants rendus par l'API, rien de recalculé.
 */
function Synthese({
  lignes,
  filtre,
  onReinitialiser,
}: {
  lignes: LigneSuiviFacture[];
  filtre: boolean;
  onReinitialiser: () => void;
}): JSX.Element | null {
  if (!filtre && lignes.length === 0) return null;
  const emises = lignes.filter((ligne) => ligne.statut === 'EMISE');
  const factures = emises.filter((ligne) => ligne.nature === 'FACTURE').reduce((somme, l) => somme + l.ttc.centimes, 0);
  const avoirs = emises.filter((ligne) => ligne.nature === 'AVOIR').reduce((somme, l) => somme + l.ttc.centimes, 0);
  const resteDu = lignes.reduce((somme, l) => somme + Math.max(0, l.solde?.resteDuCentimes ?? 0), 0);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 text-xs text-codialis-gray sm:px-6">
      <p className="min-w-0">
        {lignes.length} pièce{lignes.length > 1 ? 's' : ''}
        {' · émis : '}
        <span className="font-semibold tabular-nums text-codialis-navy">{formaterCentimes(factures)}</span>
        {avoirs > 0 && (
          <>
            {' · avoirs : '}
            <span className="font-semibold tabular-nums text-codialis-navy">− {formaterCentimes(avoirs)}</span>
          </>
        )}
        {' · reste dû : '}
        <span className="font-semibold tabular-nums text-codialis-navy">{formaterCentimes(resteDu)}</span>
      </p>
      {filtre && (
        <button
          type="button"
          onClick={onReinitialiser}
          className="inline-flex min-h-[40px] items-center gap-1 rounded-lg px-2 font-semibold text-codialis-sky hover:bg-codialis-sky/5"
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden />
          Réinitialiser les filtres
        </button>
      )}
    </div>
  );
}

/**
 * Ce que l'alerte « Facture à émettre » annonce et qui n'a pas encore de
 * brouillon : il se prépare dans « Financeurs & OPCO » (TF-413, TF-416,
 * TF-418). Lu sur la même source que cet écran-là.
 */
function BandeauAPreparer({ site }: { site: string }): JSX.Element | null {
  const { data } = useFinancements(site);
  const compteurs = data?.compteurs;
  if (!compteurs) return null;
  const {
    echeancesAFacturer: echeances,
    moisAFacturer: mois,
    finsDeParcoursAFacturer: fins,
    premiersEquipementsAFacturer: equipements,
    avoirsAEtablir: avoirs,
  } = compteurs;
  if (echeances + mois + fins + equipements + avoirs === 0) return null;
  const titres = [
    echeances > 0 ? `${echeances} échéance${echeances > 1 ? 's' : ''} de contrat d’apprentissage` : null,
    mois > 0 ? `${mois} mois d’heures de contrat de professionnalisation` : null,
    fins > 0 ? `${fins} fin${fins > 1 ? 's' : ''} de parcours France Travail` : null,
    equipements > 0 ? `${equipements} frais de 1er équipement` : null,
    avoirs > 0 ? `${avoirs} avoir${avoirs > 1 ? 's' : ''} partiel${avoirs > 1 ? 's' : ''} à établir` : null,
  ].filter((titre): titre is string => titre !== null);
  return (
    <div className="flex flex-col items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 sm:flex-row sm:items-center sm:px-5">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
        <BellRing className="h-5 w-5" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-amber-900">À facturer : {titres.join(', ')}</div>
        <p className="text-xs text-amber-800">
          Arrivés à leur dernier jour sans facture émise : la facture se prépare depuis le dossier.
        </p>
      </div>
      <Link
        to="/financing?etat=A_FACTURER"
        className="inline-flex min-h-[44px] items-center justify-center rounded-xl border border-amber-300 bg-white px-4 py-2.5 text-sm font-semibold text-amber-900 transition hover:bg-amber-100"
      >
        Voir dans Financeurs &amp; OPCO
      </Link>
    </div>
  );
}

/** Numéro — ou brouillon —, nature et objet, période facturée, émission et échéance. */
function Piece({ ligne, onOuvrir }: { ligne: LigneSuiviFacture; onOuvrir: () => void }): JSX.Element {
  return (
    <div className="min-w-0 space-y-0.5">
      <button
        type="button"
        onClick={onOuvrir}
        className={`text-left font-semibold text-codialis-navy hover:text-codialis-sky hover:underline ${
          ligne.numero ? 'font-mono' : ''
        }`}
      >
        {ligne.numero ?? `${ligne.natureLibelle} en préparation`}
      </button>
      <div className="text-xs text-codialis-gray">
        {ligne.numero ? `${ligne.natureLibelle} · ` : ''}
        {ligne.objetLibelle}
      </div>
      {ligne.typeFacturation && <div className="text-[11px] text-codialis-gray">{ligne.typeFacturation.libelle}</div>}
      <div className="text-[11px] text-codialis-gray">Période {ligne.periode.texte}</div>
      {ligne.emiseLe && (
        <div className="text-[11px] text-codialis-gray">
          Émise le {jourFrancais(ligne.emiseLe)}
          {ligne.dateEcheancePaiement ? ` · échéance ${jourFrancais(ligne.dateEcheancePaiement)}` : ''}
        </div>
      )}
      {ligne.origine && (
        <div className="text-[11px] text-codialis-gray">
          Annule <span className="font-mono">{ligne.origine.numero}</span>
        </div>
      )}
    </div>
  );
}

function Apprenant({ ligne }: { ligne: LigneSuiviFacture }): JSX.Element {
  return (
    <div className="min-w-0 space-y-0.5">
      <Link
        to={`/adm-dossier/${ligne.apprenant.id}`}
        className="font-semibold text-codialis-navy hover:text-codialis-sky hover:underline"
      >
        {ligne.apprenant.prenom} {ligne.apprenant.nom}
      </Link>
      {ligne.formation && <div className="break-words text-xs text-codialis-gray">{ligne.formation}</div>}
      <PastilleSite site={ligne.siteCode} />
    </div>
  );
}

function Client({ ligne }: { ligne: LigneSuiviFacture }): JSX.Element {
  return (
    <div className="min-w-0 space-y-0.5 text-sm">
      <div className="break-words text-codialis-navy">{ligne.client.nom ?? 'Payeur non désigné'}</div>
      {ligne.client.typeLibelle && <div className="text-[11px] text-codialis-gray">{ligne.client.typeLibelle}</div>}
      {ligne.reference && <div className="break-all font-mono text-[11px] text-codialis-gray">{ligne.reference}</div>}
    </div>
  );
}

function Montant({ ligne }: { ligne: LigneSuiviFacture }): JSX.Element {
  const avoir = ligne.nature === 'AVOIR';
  return (
    <div className="shrink-0 text-right">
      <div className="whitespace-nowrap font-semibold tabular-nums text-codialis-navy">
        {avoir ? `− ${ligne.ttc.texte}` : ligne.ttc.texte}
      </div>
      {ligne.solde && ligne.solde.resteDuCentimes > 0 && ligne.solde.resteDuCentimes !== ligne.ttc.centimes && (
        <div className="whitespace-nowrap text-[11px] tabular-nums text-codialis-gray">
          reste dû {ligne.solde.resteDu}
        </div>
      )}
    </div>
  );
}

/** État de paiement déduit, et envoi au client noté à la main. */
function Etats({ ligne }: { ligne: LigneSuiviFacture }): JSX.Element {
  return (
    <div className="min-w-0 space-y-1">
      <div className="flex flex-wrap items-center gap-1.5">
        <Pastille texte={ligne.situation.libelle} ton={ligne.situation.ton} />
        {ligne.envoi && <Pastille texte={ligne.envoi.libelle} ton={ligne.envoi.ton} />}
      </div>
      {ligne.situation.detail && (
        <p className={`text-[11px] ${ligne.situation.enRetard ? 'font-semibold text-red-700' : 'text-codialis-gray'}`}>
          {ligne.situation.detail}
        </p>
      )}
      {ligne.envoi?.detail && <p className="text-[11px] text-codialis-gray">{ligne.envoi.detail}</p>}
    </div>
  );
}
